import type {
  TaskListDefinition,
  TaskSectionDefinition,
  TaskListReference,
  TaskFieldOverride,
  TaskSectionOverride,
  TaskFieldCondition,
} from '@techgitter/contracts';
import { isTaskListReference } from '@techgitter/contracts';
import type { AdditionalDetailsValues, TemplateCustomerContext } from '../types/ticketTypeTemplate';

export type ContextKeyHint =
  | { kind: 'boolean' }
  | { kind: 'enum'; options: Array<string | number | boolean> }
  | { kind: 'text' };

export interface ReferencedContextKey {
  key: string;
  hint: ContextKeyHint;
}

export interface ReferencedContextKeys {
  fieldValueKeys: ReferencedContextKey[];
  customerContextKeys: ReferencedContextKey[];
}

export type MissingContextKeys = ReferencedContextKeys;

/**
 * Walk a TaskListDefinition (recursively through any TaskListReference
 * sections) and collect every fieldKey referenced by a `showWhen`
 * condition, bucketed by condition type. Used to prompt the user for
 * any context the conditions need that isn't already in scope.
 *
 * Each key is paired with a hint inferred from the conditions that
 * reference it — boolean (all values are booleans), enum (any `in`
 * condition, or multiple distinct equals/notEquals scalars), or text
 * (fallback).
 *
 * Cycle-safe via the visited set — mirrors the protection in
 * `flattenReferences` in checklistEngine.
 */
export function collectReferencedContextKeys(
  taskList: TaskListDefinition,
  catalog: TaskListDefinition[],
): ReferencedContextKeys {
  return collectReferencedContextKeysFromSections(
    taskList.sections,
    catalog,
    new Set([taskList.taskListId]),
  );
}

/**
 * Section-list variant — used by `TemplateTaskConfig` (which has the
 * same `Array<TaskSectionDefinition | TaskListReference>` shape but no
 * enclosing `taskListId`).
 */
export function collectReferencedContextKeysFromSections(
  sections: Array<TaskSectionDefinition | TaskListReference>,
  catalog: TaskListDefinition[],
  initialVisited: Set<string> = new Set(),
): ReferencedContextKeys {
  const fieldConditions = new Map<string, TaskFieldCondition[]>();
  const customerConditions = new Map<string, TaskFieldCondition[]>();
  const catalogMap = new Map(catalog.map(tl => [tl.taskListId, tl]));

  const record = (cond: TaskFieldCondition | null | undefined) => {
    if (!cond) return;
    // taskOutcome conditions read another task's live state, not any ticket
    // field or customer attribute — there is nothing to prompt the tech for.
    if (cond.type === 'taskOutcome') return;
    if (!cond.fieldKey) return;
    const bucket = cond.type === 'fieldValue' ? fieldConditions : customerConditions;
    const list = bucket.get(cond.fieldKey) ?? [];
    list.push(cond);
    bucket.set(cond.fieldKey, list);
  };

  const recordTaskOverrides = (overrides?: TaskFieldOverride[]) => {
    for (const o of overrides ?? []) record(o.showWhen);
  };

  const recordSectionOverride = (override: TaskSectionOverride) => {
    record(override.showWhen);
    recordTaskOverrides(override.taskOverrides);
    for (const t of override.addTasks ?? []) record(t.showWhen);
  };

  const visitSection = (section: TaskSectionDefinition) => {
    record(section.showWhen);
    for (const t of section.tasks) record(t.showWhen);
  };

  const visit = (
    items: Array<TaskSectionDefinition | TaskListReference>,
    visited: Set<string>,
  ) => {
    for (const entry of items) {
      if (isTaskListReference(entry)) {
        record(entry.showWhen);
        for (const so of entry.sectionOverrides ?? []) recordSectionOverride(so);
        for (const s of entry.addSections ?? []) visitSection(s);
        if (visited.has(entry.taskListId)) continue;
        const target = catalogMap.get(entry.taskListId);
        if (!target) continue;
        const nextVisited = new Set(visited);
        nextVisited.add(entry.taskListId);
        visit(target.sections, nextVisited);
      } else {
        visitSection(entry);
      }
    }
  };

  visit(sections, initialVisited);

  const toReferenced = (map: Map<string, TaskFieldCondition[]>): ReferencedContextKey[] =>
    [...map.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([key, conds]) => ({ key, hint: inferHint(conds) }));

  return {
    fieldValueKeys: toReferenced(fieldConditions),
    customerContextKeys: toReferenced(customerConditions),
  };
}

/**
 * Decide which input control a prompt should render for a given key,
 * based on every condition that references it.
 *
 * Rules (first match wins):
 * 1. Any `in` condition with an array value → enum dropdown with the
 *    union of all `in` arrays (plus any singleton equals/notEquals
 *    values added on top).
 * 2. Every condition's value is a boolean → checkbox.
 * 3. Two or more distinct equals/notEquals scalar values → enum.
 * 4. Otherwise → free-text input.
 */
function inferHint(conds: TaskFieldCondition[]): ContextKeyHint {
  const allValues: Array<string | number | boolean> = [];
  const scalarEqualityValues: Array<string | number | boolean> = [];
  let hasInOperator = false;
  const inOptions: Array<string | number | boolean> = [];

  for (const c of conds) {
    if (c.operator === 'in' && Array.isArray(c.value)) {
      hasInOperator = true;
      for (const v of c.value) {
        inOptions.push(v);
        allValues.push(v);
      }
    } else if (!Array.isArray(c.value)) {
      allValues.push(c.value);
      if (c.operator === 'equals' || c.operator === 'notEquals') {
        scalarEqualityValues.push(c.value);
      }
    }
  }

  if (hasInOperator) {
    // Surface singleton equals values too so the dropdown isn't missing
    // a value the author wrote elsewhere.
    return { kind: 'enum', options: dedupePreserveOrder([...inOptions, ...scalarEqualityValues]) };
  }

  if (allValues.length > 0 && allValues.every(v => typeof v === 'boolean')) {
    return { kind: 'boolean' };
  }

  const distinctScalars = dedupePreserveOrder(scalarEqualityValues);
  if (distinctScalars.length >= 2) {
    return { kind: 'enum', options: distinctScalars };
  }

  return { kind: 'text' };
}

function dedupePreserveOrder<T extends string | number | boolean>(values: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const v of values) {
    const k = typeof v + ':' + String(v);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/**
 * Return the subset of referenced keys that have no value in the
 * current context. A key is "missing" when the value is `undefined`,
 * `null`, or an empty string — those are the cases where a condition
 * would silently evaluate false.
 */
export function getMissingContextKeys(
  referenced: ReferencedContextKeys,
  additionalDetails: AdditionalDetailsValues,
  customer: TemplateCustomerContext,
): MissingContextKeys {
  const isMissing = (v: unknown) => v === undefined || v === null || v === '';
  return {
    fieldValueKeys: referenced.fieldValueKeys.filter(r => isMissing(additionalDetails[r.key])),
    customerContextKeys: referenced.customerContextKeys.filter(r => isMissing(customer[r.key])),
  };
}

/**
 * Best-effort coercion of a raw text input into the value shape that
 * condition operators expect. Booleans and numbers are inferred from
 * the string contents; everything else stays as a string. Only used
 * for the text-input branch of the prompt — boolean/enum branches
 * carry their typed value directly.
 */
export function coerceContextValue(raw: string): string | number | boolean {
  const trimmed = raw.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed !== '' && !Number.isNaN(Number(trimmed))) {
    const n = Number(trimmed);
    if (Number.isFinite(n) && String(n) === trimmed) return n;
  }
  return raw;
}
